import { NodeEdge } from "@hex-insights/app-modules";
import { NullablePick } from "@hex-insights/core";
import {
	Employee,
	EmployeeRole,
	Guardian,
	mergeFilterInputs,
	MessageAvailabilityPeriodTimingFragment,
	Person,
	PersonNameAndImageFragment,
	User,
	UserFilterInput,
	UserGroup,
	UserGroupFilterInput,
	UserStatus,
	useUserAndUserGroupListForMessagesQuery,
	useUserListForMessagesQuery,
} from "../../../../../Utilities";

export type MessagingUser = Pick<User, "id" | "email" | "isCurrent"> & {
	person:
		| (Pick<Person, "id"> &
				PersonNameAndImageFragment & {
					employee: NullablePick<Person["employee"], "jobTitle">;
					guardian: NullablePick<Person["guardian"], "id">;
				})
		| null;
	messageAvailabilityPeriods: MessageAvailabilityPeriodTimingFragment[];
};

const nondisabledUserFilter: UserFilterInput = {
	statusNEQ: UserStatus.Disabled,
};

const populatedUserGroupFilter: UserGroupFilterInput = {
	members: [nondisabledUserFilter],
};

export function useUserRecipients(filters?: UserFilterInput) {
	return useUserListForMessagesQuery({
		variables: { filters: filters ? mergeFilterInputs(filters, nondisabledUserFilter) : nondisabledUserFilter },
	});
}

export function useUserAndUserGroupRecipients(
	search: string | null | undefined,
	userFilter?: UserFilterInput,
	userGroupFilter?: UserGroupFilterInput,
) {
	const fullUserFilter: UserFilterInput = { search };
	if (userFilter) {
		fullUserFilter.and = [userFilter, nondisabledUserFilter];
	} else {
		fullUserFilter.and = [nondisabledUserFilter];
	}

	const fullUserGroupFilter: UserGroupFilterInput = { search };
	if (userGroupFilter) {
		fullUserGroupFilter.and = [userGroupFilter, populatedUserGroupFilter];
	} else {
		fullUserGroupFilter.and = [populatedUserGroupFilter];
	}

	return useUserAndUserGroupListForMessagesQuery({
		variables: {
			userFilters: fullUserFilter,
			userGroupFilters: fullUserGroupFilter,
		},
	});
}

export function splitRecipientsByRole<
	T extends {
		person: {
			employee: Pick<Employee, "role"> | null;
			guardian?: Pick<Guardian, "id"> | null;
		} | null;
	},
>(usersData: { userConnection: { edges: NodeEdge<T>[] } } | null | undefined) {
	if (!usersData) {
		return { teachers: [], administration: [], guardians: [] };
	}
	const teachers = [];
	const administration = [];
	const guardians = [];

	for (let i = 0; i < usersData.userConnection.edges.length; i++) {
		const user = usersData.userConnection.edges[i].node;
		if (user.person?.employee) {
			switch (user.person?.employee?.role) {
				case EmployeeRole.Teacher:
					teachers.push(user);
					continue;
				default:
					administration.push(user);
					continue;
			}
		}
		if (user.person?.guardian) {
			guardians.push(user);
			continue;
		}
	}

	return { teachers, administration, guardians };
}

export function splitRecipientsByRoleForStudent<
	T extends {
		person: {
			employee: Pick<Employee, "id" | "role"> | null;
			guardian?: Pick<Guardian, "id"> | null;
		} | null;
	},
>(
	usersData: { userConnection: { edges: NodeEdge<T>[] } } | null | undefined,
	guardianIDs: Guardian["id"][],
	teacherIDs: Employee["id"][],
) {
	if (!usersData) {
		return {
			relatedGuardians: [],
			relatedTeachers: [],
			teachers: [],
			administration: [],
			financialTeam: [],
			guardians: [],
		};
	}
	const relatedGuardians = [];
	const relatedTeachers = [];

	for (let i = 0; i < usersData.userConnection.edges.length; i++) {
		const user = usersData.userConnection.edges[i].node;
		if (guardianIDs.includes(user.person?.guardian?.id ?? "")) {
			relatedGuardians.push(user);
			continue;
		}
		if (teacherIDs.includes(user.person?.employee?.id ?? "")) {
			relatedTeachers.push(user);
			continue;
		}
	}

	return { relatedGuardians, relatedTeachers };
}

export function splitUserGroups<T extends Pick<UserGroup, "id"> & { members: Pick<User, "id" | "isCurrent">[] }>(
	userGroupsData: { userGroupConnection: { edges: NodeEdge<T>[] } } | null | undefined,
) {
	const viewerGroups: T[] = [];
	const otherGroups: T[] = [];

	if (!userGroupsData) {
		return { viewerGroups, otherGroups };
	}

	for (let i = 0; i < userGroupsData.userGroupConnection.edges.length; i++) {
		const userGroup = userGroupsData.userGroupConnection.edges[i].node;
		if (userGroup.members.some((member) => member.isCurrent)) {
			viewerGroups.push(userGroup);
		} else {
			otherGroups.push(userGroup);
		}
	}

	return { viewerGroups, otherGroups };
}
